import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

// Components
import Header from '../components/landing/Header';
import ProfileInfo from '../components/auth/ProfileInfo';
import OrderHistory from '../components/auth/OrderHistory';
import Footer from '../components/landing/Footer';
import { TabView, TabPanel } from 'primereact/tabview';

const ProfilePage = () => {
    const { theme, toggleTheme } = useTheme();
    const { user, isAuthenticated, loading } = useAuth();
    const [activeTab, setActiveTab] = useState(0);

    // Mostrar loading mientras se verifica la autenticación
    if (loading) {
        return (
            <div className={`landing-page theme-${theme}`}>
                <Header theme={theme} onToggleTheme={toggleTheme} />
                <main className="landing-main">
                    <div className="container py-6">
                        <div className="flex justify-content-center">
                            <div className="pi pi-spinner pi-spin text-4xl"></div>
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // Redirigir si no está autenticado
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ returnTo: '/perfil' }} replace />;
    }

    return (
        <div className={`landing-page theme-${theme}`}>
            <Helmet>
                <title>Mi Perfil - WINS Technology</title>
                <meta name="description" content="Gestiona tu perfil, revisa tu historial de pedidos y actualiza tu información personal en WINS Technology." />
                <meta property="og:title" content="Mi Perfil - WINS Technology" />
                <meta property="og:description" content="Gestiona tu perfil y revisa tu historial de pedidos." />
                <meta property="og:type" content="website" />
                <link rel="canonical" href={`${process.env.REACT_APP_SITE_URL}/perfil`} />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <Header theme={theme} onToggleTheme={toggleTheme} />
            
            <main className="landing-main">
                <div className="container py-6">
                    <div className="mb-4">
                        <h1 className="text-4xl font-bold mb-2">Mi Perfil</h1>
                        <p className="text-color-secondary">
                            Bienvenido, {user?.nombre || user?.email}
                        </p>
                    </div>

                    <div className="surface-card border-round p-4">
                        <TabView 
                            activeIndex={activeTab} 
                            onTabChange={(e) => setActiveTab(e.index)}
                        >
                            <TabPanel header="Información Personal" leftIcon="pi pi-user mr-2">
                                <div className="py-4">
                                    <ProfileInfo user={user} />
                                </div>
                            </TabPanel>
                            
                            <TabPanel header="Historial de Pedidos" leftIcon="pi pi-shopping-bag mr-2">
                                <div className="py-4">
                                    <OrderHistory userId={user?.id} />
                                </div>
                            </TabPanel>
                            
                            <TabPanel header="Configuración" leftIcon="pi pi-cog mr-2">
                                <div className="py-4">
                                    <h3 className="text-2xl font-semibold mb-4">Configuración de Cuenta</h3>
                                    
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <div className="surface-section border-round p-4 mb-4">
                                                <h4 className="text-lg font-semibold mb-2">Preferencias de Tema</h4>
                                                <p className="text-color-secondary mb-3">
                                                    Personaliza la apariencia de la aplicación
                                                </p>
                                                <div className="flex align-items-center">
                                                    <span className="mr-3">Tema actual: {theme === 'light' ? 'Claro' : 'Oscuro'}</span>
                                                    <button 
                                                        onClick={toggleTheme}
                                                        className="p-button p-button-outlined"
                                                    >
                                                        <i className={`pi ${theme === 'light' ? 'pi-moon' : 'pi-sun'} mr-2`}></i>
                                                        Cambiar a {theme === 'light' ? 'Oscuro' : 'Claro'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="col-12 md:col-6">
                                            <div className="surface-section border-round p-4 mb-4">
                                                <h4 className="text-lg font-semibold mb-2">Notificaciones</h4>
                                                <p className="text-color-secondary mb-3">
                                                    Gestiona tus preferencias de notificación
                                                </p>
                                                <div className="flex flex-column gap-2">
                                                    <label className="flex align-items-center">
                                                        <input type="checkbox" className="mr-2" defaultChecked />
                                                        Notificaciones de pedidos
                                                    </label>
                                                    <label className="flex align-items-center">
                                                        <input type="checkbox" className="mr-2" defaultChecked />
                                                        Ofertas y promociones
                                                    </label>
                                                    <label className="flex align-items-center">
                                                        <input type="checkbox" className="mr-2" />
                                                        Newsletter semanal
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                        </TabView>
                    </div>
                </div>
            </main>
            
            <Footer />
        </div>
    );
};

export default ProfilePage;
